/**
 * Heron Preston Worldwide Network — guestbook.
 *
 * A port of the L.E.D. Studio guestbook (React) to this theme's idiom: one
 * custom element, no framework, no build step. Reads and writes the guestbook
 * API; see docs/prd/guestbook-api-contract.md.
 *
 * Deliberate differences from the original:
 *  - Views swap in place instead of L.E.D.'s scroll-between-two-full-viewport-
 *    panels + IntersectionObserver. Inside a theme with a header, that scroll
 *    trick fights the page around it.
 *  - Exactly one card element exists and is moved between the overlay and the
 *    map info window, rather than a card per row. The card art is a 1.2MB PNG,
 *    so it is fetched and decoded once.
 */

const DATE_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const MAX_MESSAGE = 140;

/*
 * Everything about how the map behaves that is a matter of taste rather than
 * correctness, with the defaults in one place. Append ?tune to the page URL
 * for a panel that adjusts these live and shows the values to copy back here.
 */
const TUNABLES = {
  focusZoom: { value: 6, min: 0, max: 14, step: 1, label: 'Zoom to on marker click (0 = pan only)' },
  clusterMaxZoom: { value: 11, min: 3, max: 16, step: 1, label: 'Cluster until zoom' },
  mosaicMax: { value: 4, min: 1, max: 4, step: 1, label: 'Photos per cluster tile' },
  markerSize: { value: 32, min: 20, max: 56, step: 2, label: 'Marker size (px)' },
  anchoredWidth: { value: 500, min: 280, max: 800, step: 10, label: 'Card width on map (px)' },
  overlayWidth: { value: 760, min: 600, max: 1600, step: 20, label: 'Card width in overlay (px)' },
  panelHeight: { value: 68, min: 40, max: 95, step: 1, label: 'Map height (vh)' },
};
const TUNE_STORAGE_KEY = 'hp-guestbook-tune';
/*
  The anchored card is given a definite pixel width so Google and the card
  agree on one number — see #showCard. Never narrower than this, and always
  this much clear of the map's edges.
*/
const ANCHORED_MIN_PX = 240;
const ANCHORED_GUTTER_PX = 24;
/** Upper bound on waiting for the map to settle after a pan. */
const IDLE_TIMEOUT_MS = 800;

const MODE_STORAGE_KEY = 'hp-guestbook-markers';
const MODES = ['grouped', 'all'];

/** Current tunable values: defaults, overridden by anything saved from the panel. */
const cfg = Object.fromEntries(Object.entries(TUNABLES).map(([key, spec]) => [key, spec.value]));
try {
  const saved = JSON.parse(window.localStorage.getItem(TUNE_STORAGE_KEY) || '{}');
  for (const key of Object.keys(cfg)) {
    if (typeof saved[key] === 'number') cfg[key] = saved[key];
  }
} catch {
  // Blocked storage just means defaults.
}
/** Keep in step with the transition in guestbook-wwn.css. */
const VIEW_TRANSITION_MS = 260;

/*
 * Column widths are applied through <colgroup> with table-layout: fixed, so
 * they never reflow as content changes. Percentages rather than pixels, so the
 * table fills its container without overflowing it. TanStack owns the row
 * model and sorting; layout stays entirely ours.
 */
const COLUMNS = [
  { id: 'photo', header: '', width: '8%', mobile: '22%', sortable: false },
  { id: 'name', header: 'Name', width: '24%', mobile: '78%', sortable: true },
  { id: 'place', header: 'Place', width: '26%', sortable: true },
  { id: 'createdAt', header: 'Signed', width: '14%', sortable: true },
  { id: 'contact', header: 'Contact', width: '28%', sortable: false },
];
/*
  On a phone the table shows the photo and the name and nothing else: four
  columns in ~360px left every one of them too narrow to read. The rest is a
  tap away — a row already opens that person's card, which carries all of it.

  Driven from the column list rather than hidden in CSS so the colgroup, the
  header and the cells cannot disagree about how many columns there are.
*/
const MOBILE_TABLE_QUERY = '(max-width: 749px)';
const onPhone = () => window.matchMedia(MOBILE_TABLE_QUERY).matches;
const visibleColumns = () => (onPhone() ? COLUMNS.filter((c) => c.mobile) : COLUMNS);
const columnWidth = (column) => (onPhone() ? column.mobile : column.width);
const PHOTO_EDGE = 500;
/** Map-marker copy, made in the browser alongside the card photo. */
const THUMB_EDGE = 64;

/*
 * The Maps script is loaded `async`, so it is usually NOT ready when these
 * elements connect. The first version checked `window.google` once and gave up
 * silently, which is why the map and the location suggestions appeared to do
 * nothing. Wait for it instead, and give up loudly.
 */
const MAPS_TIMEOUT_MS = 10000;
let mapsAuthFailed = false;

window.gm_authFailure = () => {
  mapsAuthFailed = true;
};

const whenMapsReady = () =>
  new Promise((resolve) => {
    if (window.google?.maps?.places) {
      resolve('ready');
      return;
    }
    const started = Date.now();
    const timer = setInterval(() => {
      if (mapsAuthFailed) {
        clearInterval(timer);
        resolve('auth');
      } else if (window.google?.maps?.places) {
        clearInterval(timer);
        resolve('ready');
      } else if (Date.now() - started > MAPS_TIMEOUT_MS) {
        clearInterval(timer);
        resolve(window.google?.maps ? 'no-places' : 'absent');
      }
    }, 150);
  });

const MAPS_MESSAGE = {
  auth: 'The map key was rejected for this domain, so suggestions are unavailable. Type your location and we will place it later.',
  absent: 'Location suggestions could not load. Type your location and we will place it later.',
  'no-places':
    'The Places library is not enabled on the map key, so suggestions are unavailable. Type your location and we will place it later.',
};

/*
 * TanStack Form (form-core) drives every form on the page: field validation,
 * touched state, and submission. Loaded once, on first use, from the URL the
 * section puts on the element — the same arrangement as table-core.
 */
let formCorePromise = null;
const formCore = (url) => {
  /*
    A failed import used to stay in this cache, so one blocked or dropped
    request disabled every form on the page for the rest of the session — and
    silently, since nothing here was watching. Forget the failure instead, so
    the next attempt actually retries.
  */
  formCorePromise ??= import(url).catch((error) => {
    formCorePromise = null;
    throw error;
  });
  return formCorePromise;
};

/** Marks a button busy: disabled, aria-busy, and a spinner in place of its label. */
const setBusy = (button, busy, label) => {
  if (!button) return;
  button.disabled = busy;
  button.setAttribute('aria-busy', String(busy));
  if (busy) {
    button.dataset.label ??= button.innerHTML;
    button.innerHTML = `<span class="guestbook-spinner" aria-hidden="true"></span>${label ? `<span>${label}</span>` : ''}`;
  } else if (button.dataset.label !== undefined) {
    button.innerHTML = button.dataset.label;
    delete button.dataset.label;
  }
};

/** How long the Connect button confirms the copy before going back to its label. */
const CONNECT_FLASH_MS = 2200;

const EMAIL_STORAGE_KEY = 'hp-guestbook-email';
const rememberedEmail = () => {
  try {
    return window.localStorage.getItem(EMAIL_STORAGE_KEY);
  } catch {
    return null;
  }
};
const rememberEmail = (email) => {
  try {
    window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
  } catch {
    // Not worth surfacing.
  }
};

/**
 * Puts text on the clipboard, returning whether it worked.
 *
 * Two attempts, because neither is reliable alone: the async clipboard API
 * needs a secure context and, in some browsers, a user gesture that has not
 * yet expired — and this runs after a network round-trip. execCommand is
 * deprecated but still works where that has lapsed.
 */
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fall through to the older path.
  }
  try {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
    document.body.append(field);
    field.select();
    const ok = document.execCommand('copy');
    field.remove();
    return ok;
  } catch {
    return false;
  }
};

/** The prefilled introduction, as on the L.E.D. site. */
const mailtoFor = (target) => {
  const subject = 'Connecting Through HP WWN';
  const body = `Hey ${target.name}!\r\n\r\nI'm connecting through the HP WWN. I figured we might have similar interests. Would love to connect!`;
  return `mailto:${encodeURIComponent(target.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : DATE_FORMAT.format(date);
};

/** The serial printed on the card. L.E.D. showed its 7-digit LED number. */
const serialOf = (entry) => (entry.id.split(':')[1] || '').slice(-7).toUpperCase();

/**
 * Writes an entry onto a card element — the overlay card, the info-window
 * card and the signing form's live preview are the same markup (see
 * snippets/guestbook-card.liquid), so this is the one place that fills it.
 */
function fillCard(card, entry) {
  const photo = card.querySelector('[data-card-photo]');
  photo.style.backgroundImage = entry.photo ? `url("${entry.photo}")` : '';
  card.style.setProperty('--gb-card-color', entry.markerColor || '#f2682e');
  card.querySelector('[data-card-name]').textContent = entry.name || '';
  card.querySelector('[data-card-place]').textContent = entry.location?.address || entry.location?.city || '';
  card.querySelector('[data-card-message]').textContent = entry.message || '';
  card.querySelector('[data-card-date]').textContent = formatDate(entry.createdAt);
  card.querySelector('[data-card-serial]').textContent = serialOf(entry);
}

/**
 * The card artwork can be replaced from the admin. A design is two images —
 * the base below the photo and the glow and lockup above it — and the feed
 * names both. A design with no upper layer clears it rather than leaving the
 * bundled one over somebody else's artwork.
 */
function applyCardArt(root, card) {
  if (!card) return;
  for (const art of root.querySelectorAll('[data-card-art]')) {
    if (card.background) art.src = card.background;
  }
  for (const art of root.querySelectorAll('[data-card-art-top]')) {
    if (card.background) art.hidden = !card.backgroundTop;
    if (card.backgroundTop) art.src = card.backgroundTop;
  }
}

class GuestbookApp extends HTMLElement {
  #entries = [];
  #map = null;
  #markers = new Map();
  #markerLib = null;
  #infoWindow = null;
  #table = null;
  #core = null;
  #panel = null;
  #view = 'map';
  #cardEntry = null;
  #connectLabel = '';
  #connectFlash = 0;
  #modal = null;
  #connectForm = null;
  #optInForm = null;
  #section = null;
  #tableSection = null;
  #sorting = [{ id: 'createdAt', desc: true }];
  #mode = 'grouped';

  connectedCallback() {
    this.endpoint = this.dataset.endpoint.replace(/\/$/, '');
    this.card = this.querySelector('[data-card]');
    this.overlay = this.querySelector('[data-overlay]');
    this.status = this.querySelector('[data-status]');
    // The table is revealed below the signing form, outside this element, so
    // everything table-related is looked up from the section wrapper.
    this.#section = this.closest('.guestbook-section') ?? document;
    this.#tableSection = this.#section.querySelector('[data-table-section]');
    for (const button of this.querySelectorAll('[data-view-btn]')) {
      button.addEventListener('click', () => this.#setView(button.dataset.viewBtn));
    }

    this.#panel = this.querySelector('.guestbook__panel');

    // A returning visitor's own choice wins; otherwise the section setting,
    // and failing that All — every signature visible rather than collapsed.
    this.#mode = this.#storedMode() || this.dataset.markerMode || 'all';
    for (const button of this.querySelectorAll('[data-mode]')) {
      button.addEventListener('click', () => this.#setMode(button.dataset.mode));
    }
    this.#reflectMode();
    this.overlay?.addEventListener('click', (event) => {
      if (event.target === this.overlay) this.#closeCard();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closeCard();
    });

    // The column set differs either side of the breakpoint, so a rotation or
    // a resize has to rebuild the table rather than just restyle it.
    window.matchMedia(MOBILE_TABLE_QUERY).addEventListener('change', () => {
      if (this.#table) this.#renderTable();
    });
    this.querySelector('[data-close-card]')?.addEventListener('click', () => this.#closeCard());

    // Connect — the panel inside the card, and the modal that follows it.
    this.#modal = this.#section.querySelector('[data-modal]');
    const connectButton = this.querySelector('[data-connect]');
    this.#connectLabel = connectButton?.textContent?.trim() ?? '';
    connectButton?.addEventListener('click', () => this.#openConnect());
    this.querySelector('[data-connect-cancel]')?.addEventListener('click', () => this.#closeConnect());
    this.querySelector('[data-connect-panel]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#connectForm?.handleSubmit();
    });
    this.#modal?.querySelector('[data-modal-close]')?.addEventListener('click', () => this.#closeModal());
    this.#modal?.addEventListener('click', (event) => {
      if (event.target === this.#modal) this.#closeModal();
    });
    this.#modal?.querySelector('[data-modal-optin]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#optInForm?.handleSubmit();
    });
    // Failures are recovered from in #openConnect; swallowing the rejection
    // here only keeps it out of the console as an unhandled one.
    this.#initForms().catch(() => {});

    this.#load();
  }

  async #load() {
    try {
      const response = await fetch(`${this.endpoint}/api/v1/entries`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      this.#entries = data.entries || [];
      applyCardArt(this.#section, data.card);
    } catch (error) {
      this.status.textContent = 'The guestbook could not be loaded. Please try again shortly.';
      this.status.hidden = false;
      console.error('[guestbook] feed failed', error);
      return;
    }

    this.status.hidden = true;
    this.#renderTable();
    // The map must exist before a permalink can anchor a card to it —
    // otherwise the deep link falls back to the overlay while a click gets
    // the anchored card, and the two look nothing alike.
    await this.#initMap();
    this.#openFromUrl();
  }

  /* ---------- table ---------- */

  /**
   * table-core is 57KB and the table is hidden until asked for, so it is
   * imported on first use from the URL Liquid put on the element. A dynamic
   * import also avoids an import map, which would otherwise have to hardcode
   * the CDN origin and could not be served by the local dev server.
   */
  async #tableCore() {
    this.#core ??= await import(this.dataset.tableCore);
    return this.#core;
  }

  #buildTable(core) {
    this.#table = core.constructTable({
      features: {
        ...core.coreFeatures,
        coreReactivityFeature: core.storeReactivityBindings(),
        rowSortingFeature: core.rowSortingFeature,
        sortedRowModel: core.createSortedRowModel(),
        sortFns: core.sortFns,
      },
      data: this.#entries,
      columns: COLUMNS.map((column) => ({
        id: column.id,
        accessorFn: (entry) => this.#cellValue(entry, column.id),
        enableSorting: column.sortable,
      })),
      getRowId: (entry) => entry.id,
      initialState: { sorting: this.#sorting },
    });
  }

  /** Sort keys. `place` and `contact` differ from what the cell displays. */
  #cellValue(entry, columnId) {
    if (columnId === 'name') return entry.name || '';
    if (columnId === 'place') return entry.location.city || entry.location.address || '';
    if (columnId === 'createdAt') return Date.parse(entry.createdAt) || 0;
    if (columnId === 'contact') return entry.instagram || entry.message || '';
    return '';
  }

  async #renderTable() {
    // One table instance; sorting is reactive, so only data changes rebuild it.
    if (!this.#table) this.#buildTable(await this.#tableCore());

    const columns = visibleColumns();

    const colgroup = this.#section.querySelector('[data-colgroup]');
    colgroup.replaceChildren(
      ...columns.map((column) => {
        const col = document.createElement('col');
        col.style.width = columnWidth(column);
        return col;
      }),
    );

    const headRow = document.createElement('tr');
    for (const column of columns) {
      const th = document.createElement('th');
      th.scope = 'col';
      if (!column.sortable) {
        th.textContent = column.header;
        headRow.append(th);
        continue;
      }

      const sorting = this.#sorting;
      const active = sorting[0]?.id === column.id;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'guestbook-table__sort';
      button.textContent = column.header;
      if (active) {
        button.classList.add('is-active');
        button.dataset.direction = sorting[0].desc ? 'desc' : 'asc';
      }
      th.setAttribute('aria-sort', active ? (sorting[0].desc ? 'descending' : 'ascending') : 'none');
      button.addEventListener('click', () => this.#sortBy(column.id));
      th.append(button);
      headRow.append(th);
    }
    this.#section.querySelector('[data-thead]').replaceChildren(headRow);

    const body = document.createDocumentFragment();
    for (const row of this.#table.getRowModel().rows) {
      body.append(this.#tableRow(row.original));
    }
    this.#section.querySelector('[data-tbody]').replaceChildren(body);
  }

  async #sortBy(columnId) {
    const current = this.#sorting[0];
    this.#sorting =
      current?.id === columnId && !current.desc ? [{ id: columnId, desc: true }] : [{ id: columnId, desc: false }];
    this.#table.setSorting(this.#sorting);
    await this.#renderTable();
  }

  #tableRow(entry) {
    const tr = document.createElement('tr');
    tr.tabIndex = 0;
    tr.addEventListener('click', () => this.#showCard(entry));
    tr.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.#showCard(entry);
      }
    });

    const cell = {
      photo: () => {
        const td = document.createElement('td');
        const thumb = document.createElement('span');
        thumb.className = 'guestbook-table__thumb';
        if (entry.photoThumb || entry.photo) {
          thumb.append(this.#thumb(entry, 48));
        } else {
          thumb.style.backgroundColor = entry.markerColor || '#f2682e';
        }
        td.append(thumb);
        return td;
      },
      name: () => {
        const td = document.createElement('td');
        td.textContent = entry.name;
        return td;
      },
      place: () => {
        const td = document.createElement('td');
        td.textContent = entry.location.city || entry.location.address || '';
        return td;
      },
      createdAt: () => {
        const td = document.createElement('td');
        td.className = 'guestbook-table__date';
        td.textContent = formatDate(entry.createdAt);
        return td;
      },
      contact: () => {
        const td = document.createElement('td');
        td.textContent = entry.instagram ? `@${entry.instagram}` : entry.message || '';
        return td;
      },
    };

    tr.append(...visibleColumns().map((column) => cell[column.id]()));
    return tr;
  }

  /* ---------- map ---------- */

  async #initMap() {
    const stage = this.querySelector('[data-stage]');
    if (!stage) return;

    const state = await whenMapsReady();
    if (state !== 'ready') {
      // The list still works without Maps, so this is a downgrade, not a
      // failure — but say so rather than showing an empty grey box.
      stage.innerHTML = '';
      const notice = document.createElement('p');
      notice.className = 'guestbook__status';
      notice.textContent = `${MAPS_MESSAGE[state]} The table below still lists everyone.`;
      stage.append(notice);
      this.#setView('list');
      return;
    }

    // Advanced markers need a Map ID, and cannot be combined with inline
    // `styles` — the styling moves into the Map ID's cloud configuration.
    this.#markerLib = await google.maps.importLibrary('marker');
    this.#map = new google.maps.Map(stage, {
      center: { lat: 25, lng: -20 },
      zoom: Number(this.dataset.zoom || 2),
      disableDefaultUI: true,
      zoomControl: true,
      // The one default control worth keeping — the old page had it too.
      fullscreenControl: true,
      fullscreenControlOptions: { position: google.maps.ControlPosition.RIGHT_TOP },
      // Bottom-right, so it clears the marker-style switcher top-right.
      zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
      mapId: this.dataset.mapId || 'DEMO_MAP_ID',
    });

    // InfoWindow handles anchoring and panning the map to fit, which is what
    // "show it above the marker" needs.
    this.#infoWindow = new google.maps.InfoWindow({
      content: '',
      disableAutoPan: false,
      // Wider than the card so Google never scrolls it; the card sets its own width.
      maxWidth: 560,
    });
    this.#map.addListener('click', () => this.#closeCard());

    // 1,080 markers at world zoom is what made the map feel heavy: every one is
    // a DOM node with its own photo. Cluster by a zoom-dependent grid instead,
    // and only draw what is actually on screen.
    this.#map.addListener('idle', () => this.#drawMarkers());
    this.#drawMarkers();
  }

  #storedMode() {
    try {
      const saved = window.localStorage.getItem(MODE_STORAGE_KEY);
      if (saved === 'photos' || saved === 'colours') return 'grouped';
      return MODES.includes(saved) ? saved : null;
    } catch {
      // Private browsing and blocked storage both throw; the default is fine.
      return null;
    }
  }

  #setMode(mode) {
    if (!MODES.includes(mode) || mode === this.#mode) return;
    this.#mode = mode;
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch {
      // Not worth surfacing — the choice just won't persist.
    }
    this.#reflectMode();

    // Every marker's appearance depends on the mode, so start clean.
    for (const marker of this.#markers.values()) marker.map = null;
    this.#markers.clear();
    this.#drawMarkers();
  }

  #reflectMode() {
    for (const button of this.querySelectorAll('[data-mode]')) {
      const active = button.dataset.mode === this.#mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
  }

  /** Grid size in degrees — coarse when zoomed out, exact when zoomed in. */
  #gridSize() {
    // "All" is the no-clustering view: every signature gets its own pin.
    if (this.#mode === 'all') return 0;
    const zoom = this.#map.getZoom() ?? 2;
    if (zoom >= cfg.clusterMaxZoom) return 0;
    return 360 / 2 ** (zoom + 1);
  }

  #drawMarkers() {
    const bounds = this.#map.getBounds();
    if (!bounds) return;

    const grid = this.#gridSize();
    const buckets = new Map();

    for (const entry of this.#entries) {
      const { lat, lng } = entry.location;
      if (typeof lat !== 'number' || typeof lng !== 'number') continue;
      if (!bounds.contains({ lat, lng })) continue;

      const key = grid === 0 ? entry.id : `${Math.floor(lat / grid)}:${Math.floor(lng / grid)}`;
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.push(entry);
      } else {
        buckets.set(key, [entry]);
      }
    }

    // Reuse what is already correct; only add and remove the difference.
    for (const [key, marker] of this.#markers) {
      if (!buckets.has(key)) {
        marker.map = null;
        this.#markers.delete(key);
      }
    }

    for (const [key, group] of buckets) {
      if (this.#markers.has(key)) continue;
      const marker = group.length === 1 ? this.#singleMarker(group[0]) : this.#clusterMarker(group);
      this.#markers.set(key, marker);
    }
  }

  /** Advanced markers are real DOM, so the rounded thumbnails are just CSS. */
  /** The marker currently showing this entry, whether alone or in a cluster. */
  #markerFor(entryId) {
    for (const marker of this.#markers.values()) {
      if (marker.entryIds?.includes(entryId)) return marker;
    }
    return null;
  }

  #place(content, position, title, onClick) {
    // Advanced markers are DOM elements, so they take gmpClickable + a
    // 'gmp-click' listener rather than the classic Marker's addListener('click').
    const marker = new this.#markerLib.AdvancedMarkerElement({
      map: this.#map,
      position,
      title,
      content,
      gmpClickable: true,
    });
    marker.addEventListener('gmp-click', onClick);
    return marker;
  }

  #thumb(entry, size) {
    const image = document.createElement('img');
    // Always the 64px copy — never the display image, there can be hundreds.
    image.src = entry.photoThumb || entry.photo;
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.width = size;
    image.height = size;
    // A marker should never render as a broken-image glyph; fall back to the
    // signer's colour, which is what a photoless entry already shows.
    image.addEventListener('error', () => {
      const holder = image.parentElement;
      image.remove();
      if (holder?.classList.contains('guestbook-pin')) {
        holder.classList.add('guestbook-pin--colour');
        holder.style.backgroundColor = entry.markerColor || '#f2682e';
      }
    });
    return image;
  }

  #singleMarker(entry) {
    const pin = document.createElement('span');
    pin.className = 'guestbook-pin';

    if (entry.photoThumb || entry.photo) {
      pin.append(this.#thumb(entry, cfg.markerSize));
    } else {
      pin.classList.add('guestbook-pin--colour');
      pin.style.backgroundColor = entry.markerColor || '#f2682e';
    }

    const marker = this.#place(pin, { lat: entry.location.lat, lng: entry.location.lng }, entry.name, () =>
      this.#focusEntry(entry),
    );
    marker.entryIds = [entry.id];
    return marker;
  }

  /** Colour mode: a plain count bubble, no images at all. */
  #countBubble(group) {
    const bubble = document.createElement('span');
    bubble.className = 'guestbook-cluster';
    bubble.textContent = String(group.length);
    const size = Math.min(46, 26 + Math.log2(group.length) * 4);
    bubble.style.width = `${size}px`;
    bubble.style.height = `${size}px`;
    return bubble;
  }

  /** Photo mode: the members' own thumbnails tiled into one marker. */
  #photoMosaic(group) {
    const withPhotos = group.filter((entry) => entry.photoThumb || entry.photo);
    if (withPhotos.length === 0) return this.#countBubble(group);

    const tiles = withPhotos.slice(0, cfg.mosaicMax);
    const mosaic = document.createElement('span');
    mosaic.className = `guestbook-mosaic guestbook-mosaic--${tiles.length}`;
    const size = Math.min(56, 34 + Math.log2(group.length) * 4);
    mosaic.style.width = `${size}px`;
    mosaic.style.height = `${size}px`;

    for (const entry of tiles) mosaic.append(this.#thumb(entry, size));

    const count = document.createElement('span');
    count.className = 'guestbook-mosaic__count';
    count.textContent = String(group.length);
    mosaic.append(count);
    return mosaic;
  }

  #clusterMarker(group) {
    const lat = group.reduce((sum, item) => sum + item.location.lat, 0) / group.length;
    const lng = group.reduce((sum, item) => sum + item.location.lng, 0) / group.length;

    const content = this.#photoMosaic(group);

    // Zoom in far enough that this cluster actually breaks apart.
    const marker = this.#place(content, { lat, lng }, `${group.length} signatures`, () => {
      this.#map.setCenter({ lat, lng });
      this.#map.setZoom(Math.min(cfg.clusterMaxZoom + 1, (this.#map.getZoom() ?? 2) + 3));
    });
    marker.entryIds = group.map((entry) => entry.id);
    return marker;
  }

  /* ---------- the card ---------- */

  #fillCard(entry) {
    fillCard(this.card, entry);
    this.#cardEntry = entry;
    this.#resetConnectLabel();
    this.card.querySelector('[data-connect]').hidden = !entry.canConnect;
    this.#closeConnect();
  }

  /**
   * Marker click: bring the map to the person first, then open the card above
   * their pin. Zooming past the cluster threshold guarantees the entry has a
   * pin of its own to anchor to.
   */
  async #focusEntry(entry) {
    const { lat, lng } = entry.location;
    if (!this.#map || typeof lat !== 'number' || typeof lng !== 'number') {
      this.#showCard(entry);
      return;
    }

    const zoom = this.#map.getZoom() ?? 2;
    const target = Math.max(zoom, cfg.focusZoom);
    const settled = new Promise((resolve) => google.maps.event.addListenerOnce(this.#map, 'idle', resolve));
    this.#map.panTo({ lat, lng });
    if (target !== zoom) this.#map.setZoom(target);
    // 'idle' only fires if the camera actually moved; if it did not, carry on.
    await Promise.race([settled, new Promise((resolve) => setTimeout(resolve, IDLE_TIMEOUT_MS))]);

    // Markers may have been redrawn, so find whichever pin now holds this
    // entry — its own, or the cluster it sits in — and hang the card off that.
    this.#showCard(entry, this.#markerFor(entry.id), { lat, lng });
  }

  /**
   * Two presentations, as L.E.D. had: clicking a marker anchors the card above
   * that pin and pans the map to it; opening from the table centres it in an
   * overlay, since there is no pin to anchor to.
   */
  #showCard(entry, anchor, position) {
    this.#fillCard(entry);
    const wrap = this.card.closest('[data-card-wrap]');

    if ((anchor || position) && this.#infoWindow) {
      this.overlay.hidden = true;
      wrap.classList.remove('guestbook-card__wrap--overlay');
      wrap.classList.add('guestbook-card__wrap--anchored');
      /*
        Google sizes the info window by measuring its content, but the card is
        a size-contained container (container-type: inline-size) taking its
        width from vw, so it reports no intrinsic width to measure. Google laid
        the window out narrower than the card painted and `.gm-style-iw-d`
        clipped the difference — which is why the card was cut off on a phone.
        Hand both the same definite pixel width instead.
      */
      const available = this.#map.getDiv()?.clientWidth || window.innerWidth;
      const width = Math.max(ANCHORED_MIN_PX, Math.min(cfg.anchoredWidth, available - ANCHORED_GUTTER_PX));
      wrap.style.width = `${width}px`;
      this.#infoWindow.setOptions({
        maxWidth: width,
        // Reset every time: an entry opened from inside a cluster sets an
        // offset below, and it used to persist onto every later marker.
        pixelOffset: anchor
          ? new google.maps.Size(0, 0)
          : // No pin to hang from: open at the coordinates, lifted by one
            // marker height so the card sits above the spot.
            new google.maps.Size(0, -cfg.markerSize),
      });
      this.#infoWindow.setContent(wrap);
      if (anchor) {
        this.#infoWindow.open({ anchor, map: this.#map });
      } else {
        this.#infoWindow.setPosition(position);
        this.#infoWindow.open({ map: this.#map });
      }
    } else {
      this.#infoWindow?.close();
      wrap.classList.remove('guestbook-card__wrap--anchored');
      // The overlay sizes the card itself; drop the width the map set.
      wrap.style.width = '';
      this.overlay.querySelector('[data-overlay-slot]').append(wrap);
      this.overlay.hidden = false;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('id', entry.id);
    window.history.replaceState({}, '', url);
  }

  /* ---------- connect ---------- */

  async #openConnect() {
    const panel = this.card.querySelector('[data-connect-panel]');
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('is-open'));
    const email = panel.querySelector('input[name="email"]');
    email.value = rememberedEmail() || '';
    email.focus({ preventScroll: true });

    /*
      The forms are loaded asynchronously at startup and that load can fail.
      It used to leave a panel that opened but could never be submitted, with
      nothing said. Try again here, and if it still fails, say so.
    */
    if (!this.#connectForm) {
      try {
        await this.#initForms();
      } catch {
        this.#connectError('Something went wrong. Please try again.');
        return;
      }
    }
    this.#connectForm?.setFieldValue('email', email.value);
  }

  #closeConnect() {
    const panel = this.card.querySelector('[data-connect-panel]');
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.hidden = true;
    this.#connectError('');
  }

  /**
   * Says something on the Connect button for a moment, then puts its own
   * label back. Used to confirm the copy, since the mailto handoff that
   * follows is silent when the visitor has no mail client configured.
   */
  #flashConnect(text) {
    const button = this.card.querySelector('[data-connect]');
    if (!button) return;
    button.textContent = text;
    window.clearTimeout(this.#connectFlash);
    this.#connectFlash = window.setTimeout(() => this.#resetConnectLabel(), CONNECT_FLASH_MS);
  }

  #resetConnectLabel() {
    window.clearTimeout(this.#connectFlash);
    const button = this.card.querySelector('[data-connect]');
    if (button && this.#connectLabel) button.textContent = this.#connectLabel;
  }

  #connectError(text) {
    const slot = this.card.querySelector('[data-connect-error]');
    slot.textContent = text;
    slot.hidden = !text;
  }

  /** The connect panel and the opt-in prompt, each a small TanStack form. */
  async #initForms() {
    const { FormApi, FieldApi } = await formCore(this.dataset.formCore);
    const panel = this.querySelector('[data-connect-panel]');
    const optin = this.#modal?.querySelector('[data-modal-optin]');
    if (!(panel && optin)) return;

    this.#connectForm = new FormApi({
      defaultValues: { email: '' },
      onSubmit: async ({ value }) => {
        if (!this.#cardEntry) return;
        const email = value.email.trim();
        rememberEmail(email);
        await this.#connect(email, this.#cardEntry);
      },
    });
    this.#connectForm.mount();
    const emailField = new FieldApi({
      form: this.#connectForm,
      name: 'email',
      validators: {
        onSubmit: ({ value }) =>
          EMAIL_PATTERN.test(value.trim()) ? undefined : "That doesn't look like an email address.",
      },
    });
    emailField.mount();
    const emailInput = panel.querySelector('input[name="email"]');
    emailInput.addEventListener('input', () => emailField.handleChange(emailInput.value));
    emailField.store.subscribe(() => this.#connectError(emailField.state.meta.errors[0] || ''));
    this.#connectForm.store.subscribe(() => {
      setBusy(panel.querySelector('[data-connect-go]'), this.#connectForm.state.isSubmitting);
    });

    this.#optInForm = new FormApi({
      defaultValues: { choice: 'yes' },
      onSubmit: async ({ value }) => {
        await this.#applyOptIn(optin.dataset.email, value.choice === 'yes');
      },
    });
    this.#optInForm.mount();
    const choiceField = new FieldApi({ form: this.#optInForm, name: 'choice' });
    choiceField.mount();
    for (const radio of optin.querySelectorAll('input[name="connectChoice"]')) {
      radio.addEventListener('change', () => choiceField.handleChange(radio.value));
    }
    this.#optInForm.store.subscribe(() => {
      setBusy(optin.querySelector('button[type="submit"]'), this.#optInForm.state.isSubmitting);
    });
  }

  /**
   * One round-trip decides everything: the server knows whether the visitor
   * is a member and whether both sides have opted in. Only the "ok" case ever
   * carries the target's address.
   */
  async #connect(email, target) {
    try {
      const response = await fetch(`${this.endpoint}/api/v1/connect`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, targetId: target.id }),
      });
      const result = await response.json().catch(() => ({}));

      if (result.status === 'ok') {
        this.#closeConnect();
        /*
          The mailto below does nothing at all on a machine with no mail
          client registered — which read as a dead button. Copy the address
          first and say so, so there is always something to show for the
          click, then still make the handoff for everyone who does have one.
        */
        const copied = await copyToClipboard(result.target.email);
        this.#flashConnect(copied ? this.dataset.connectCopied || 'Copied Email' : result.target.email);
        window.location.href = mailtoFor(result.target);
      } else if (result.status === 'unknown') {
        this.#showModal('form', email);
      } else if (result.status === 'not_opted_in') {
        this.#showModal('optin', email);
      } else if (response.status === 404) {
        this.#connectError(this.dataset.connectUnavailable || 'This member cannot be contacted right now.');
      } else {
        this.#connectError(result.error || 'Something went wrong. Please try again.');
      }
    } catch {
      this.#connectError('Something went wrong. Please try again.');
    }
  }

  /* ---------- the modal: "not registered" hosts the real form ---------- */

  #showModal(kind, email) {
    const modal = this.#modal;
    if (!modal) return;
    const title = modal.querySelector('[data-modal-title]');
    const formSlot = modal.querySelector('[data-modal-form-slot]');
    const optin = modal.querySelector('[data-modal-optin]');
    const form = this.#section.querySelector('guestbook-form');

    if (kind === 'form' && form) {
      title.textContent = modal.dataset.titleNotRegistered;
      // Move the one real form in, rather than keeping a second copy in sync.
      form.dataset.home = form.dataset.home || 'section';
      formSlot.append(form);
      formSlot.hidden = false;
      optin.hidden = true;
      form.prefillEmail?.(email);
      form.onSigned = (entry) => {
        this.#closeModal();
        // They are registered now — run the same check again with the
        // email they just gave, which resolves to a mailto or the opt-in prompt.
        if (this.#cardEntry) this.#connect(email, this.#cardEntry);
        this.addEntry(entry, { quiet: true });
      };
    } else {
      title.textContent = modal.dataset.titleNotOptedIn;
      formSlot.hidden = true;
      optin.hidden = false;
      optin.dataset.email = email;
    }

    modal.hidden = false;
    document.body.classList.add('guestbook-modal-open');
    modal.querySelector('[data-modal-close]').focus({ preventScroll: true });
  }

  #closeModal() {
    const modal = this.#modal;
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.classList.remove('guestbook-modal-open');

    // Put the form back below the map where it lives.
    const form = modal.querySelector('guestbook-form');
    if (form) {
      form.onSigned = null;
      this.#section.querySelector('[data-form-home]')?.append(form);
    }
  }

  async #applyOptIn(email, choice) {
    try {
      const response = await fetch(`${this.endpoint}/api/v1/connect/opt-in`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, optIn: choice }),
      });
      const result = await response.json().catch(() => ({}));
      this.#closeModal();
      if (result.status === 'ok' && choice && this.#cardEntry) {
        await this.#connect(email, this.#cardEntry);
      } else if (!choice) {
        this.#closeConnect();
      }
    } catch {
      this.#connectError('Something went wrong. Please try again.');
    }
  }

  #closeCard() {
    this.overlay.hidden = true;
    this.#infoWindow?.close();
    // Bring the card home so the next open finds it wherever it needs it.
    const wrap = this.card.closest('[data-card-wrap]');
    wrap.classList.remove('guestbook-card__wrap--anchored');
    this.overlay.querySelector('[data-overlay-slot]').append(wrap);
    const url = new URL(window.location.href);
    url.searchParams.delete('id');
    window.history.replaceState({}, '', url);
  }

  /** Deep link — this is what an NFC badge resolves to. */
  #openFromUrl() {
    const id = new URLSearchParams(window.location.search).get('id');
    if (!id) return;

    const entry = this.#entries.find((item) => item.id === id);
    if (entry) {
      const { lat, lng } = entry.location;
      if (this.#map && typeof lat === 'number' && typeof lng === 'number') {
        this.#focusEntry(entry);
        return;
      }
      this.#showCard(entry);
      return;
    }

    // Not in the feed: it may be pending review, which lookup still resolves.
    this.#fetchOne(id);
  }

  async #fetchOne(id) {
    try {
      const response = await fetch(`${this.endpoint}/api/v1/entries/${encodeURIComponent(id)}`);
      if (!response.ok) return;
      this.#focusEntry(await response.json());
    } catch (error) {
      console.error('[guestbook] lookup failed', error);
    }
  }

  /**
   * Cross-fades between map and list. `hidden` is cleared before the incoming
   * view animates and reapplied to the outgoing one afterwards, so both are
   * paintable for the duration — otherwise the outgoing view would simply
   * disappear and only one half of the transition would be visible.
   */
  /**
   * The table is always on the page, below the form. "Map" and "List" scroll
   * to the one you pick; the pressed state follows.
   */
  #setView(view) {
    if (!this.#tableSection) return;
    this.#view = view;
    for (const button of this.querySelectorAll('[data-view-btn]')) {
      const active = button.dataset.viewBtn === view;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    }
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const target = view === 'list' ? this.#tableSection : (this.#section.querySelector('.guestbook__bar') ?? this);
    target.scrollIntoView({ behavior, block: 'start' });
  }

  /** Rebuild every marker with the current tunables. */
  redraw() {
    if (!this.#map) return;
    for (const marker of this.#markers.values()) marker.map = null;
    this.#markers.clear();
    this.#drawMarkers();
  }

  addEntry(entry, { quiet = false } = {}) {
    this.#entries.unshift(entry);
    if (this.#table) {
      this.#table = null;
      this.#renderTable();
    }
    if (!quiet) this.#showCard(entry);
  }
}

customElements.define('guestbook-app', GuestbookApp);

/*
 * Live tuning panel, only when the URL carries ?tune. Every slider writes the
 * value into `cfg`, applies the CSS-driven ones as custom properties, saves to
 * localStorage, and redraws the map. The JSON at the bottom is what to paste
 * back into TUNABLES once a setting feels right.
 */
class GuestbookTune extends HTMLElement {
  connectedCallback() {
    if (!new URLSearchParams(window.location.search).has('tune')) {
      this.remove();
      return;
    }
    this.className = 'guestbook-tune';
    this.hidden = false;
    this.innerHTML = `
      <div class="guestbook-tune__head">
        <strong>Tune</strong>
        <button type="button" class="guestbook-tune__reset" data-reset>Reset</button>
      </div>
      <div data-rows></div>
      <textarea class="guestbook-tune__out" data-out rows="4" readonly aria-label="Current values"></textarea>
    `;

    const rows = this.querySelector('[data-rows]');
    for (const [key, spec] of Object.entries(TUNABLES)) {
      const row = document.createElement('label');
      row.className = 'guestbook-tune__row';
      row.innerHTML = `<span>${spec.label}</span><output data-out-for="${key}">${cfg[key]}</output>`;
      const input = document.createElement('input');
      input.type = 'range';
      input.min = spec.min;
      input.max = spec.max;
      input.step = spec.step;
      input.value = cfg[key];
      input.addEventListener('input', () => this.#set(key, Number(input.value)));
      row.append(input);
      rows.append(row);
    }

    this.querySelector('[data-reset]').addEventListener('click', () => {
      for (const [key, spec] of Object.entries(TUNABLES)) cfg[key] = spec.value;
      try {
        window.localStorage.removeItem(TUNE_STORAGE_KEY);
      } catch {
        // ignore
      }
      for (const input of this.querySelectorAll('input')) {
        const key = input.closest('label').querySelector('output').dataset.outFor;
        input.value = cfg[key];
      }
      this.#apply();
    });

    this.#apply();
  }

  #set(key, value) {
    cfg[key] = value;
    try {
      window.localStorage.setItem(TUNE_STORAGE_KEY, JSON.stringify(cfg));
    } catch {
      // ignore
    }
    this.#apply();
  }

  #apply() {
    for (const [key, value] of Object.entries(cfg)) {
      const out = this.querySelector(`[data-out-for="${key}"]`);
      if (out) out.textContent = value;
    }
    applyCssTunables();
    this.querySelector('[data-out]').value = JSON.stringify(cfg, null, 2);
    document.querySelector('guestbook-app')?.redraw();
  }
}

/** The tunables that are pure CSS live as custom properties on the section. */
function applyCssTunables() {
  const section = document.querySelector('.guestbook-section');
  if (!section) return;
  section.style.setProperty('--gb-anchored-width', `${cfg.anchoredWidth}px`);
  section.style.setProperty('--gb-overlay-width', `${cfg.overlayWidth}px`);
  section.style.setProperty('--gb-marker-size', `${cfg.markerSize}px`);
  section.style.setProperty('--gb-panel-height', `clamp(420px, ${cfg.panelHeight}vh, 900px)`);
}

customElements.define('guestbook-tune', GuestbookTune);
applyCssTunables();

/**
 * Pan-and-zoom cropping against a fixed square frame.
 *
 * The frame is square because the card's photo well is: a resizable rectangle
 * would only let someone choose a shape that then gets squashed to fit. The
 * image moves behind the frame instead, which is the avatar-cropper
 * interaction people already know.
 *
 * `scale` is relative to "just covers the frame", so 1 is always a valid
 * photo and the image can never be dragged away from the edges.
 */
class SquareCropper {
  #stage;
  #media = null;
  #natural = { height: 0, width: 0 };
  #zoom = 1;
  #x = 0;
  #y = 0;
  #drag = null;
  #onZoom = null;

  constructor(stage, { onZoom } = {}) {
    this.#stage = stage;
    this.#onZoom = onZoom;
    stage.addEventListener('pointerdown', (event) => this.#start(event));
    stage.addEventListener('pointermove', (event) => this.#move(event));
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) {
      stage.addEventListener(type, () => this.#end());
    }
    stage.addEventListener('wheel', (event) => {
      if (!this.#media) return;
      event.preventDefault();
      this.zoom(this.#zoom * (event.deltaY < 0 ? 1.06 : 1 / 1.06));
    });
  }

  get ready() {
    return this.#media !== null;
  }

  /** `media` is an <img> or a <video>; both expose intrinsic dimensions. */
  attach(media, width, height) {
    this.#media = media;
    this.#natural = { height, width };
    this.#zoom = 1;
    this.#x = 0;
    this.#y = 0;
    this.#centre();
  }

  detach() {
    this.#media = null;
  }

  get #edge() {
    return this.#stage.clientWidth;
  }

  /** Scale at which the image exactly covers the frame. */
  get #base() {
    const { width, height } = this.#natural;
    return width && height ? this.#edge / Math.min(width, height) : 1;
  }

  get #scale() {
    return this.#base * this.#zoom;
  }

  #centre() {
    const scale = this.#scale;
    this.#x = (this.#edge - this.#natural.width * scale) / 2;
    this.#y = (this.#edge - this.#natural.height * scale) / 2;
    this.#apply();
  }

  zoom(next) {
    if (!this.#media) return;
    const clamped = Math.min(4, Math.max(1, next));
    // Keep the frame's centre pinned while the scale changes.
    const centre = this.#edge / 2;
    const ratio = (this.#base * clamped) / this.#scale;
    this.#x = centre - (centre - this.#x) * ratio;
    this.#y = centre - (centre - this.#y) * ratio;
    this.#zoom = clamped;
    this.#apply();
    this.#onZoom?.(clamped);
  }

  #start(event) {
    if (!this.#media) return;
    this.#drag = { x: event.clientX - this.#x, y: event.clientY - this.#y };
    this.#stage.setPointerCapture?.(event.pointerId);
  }

  #move(event) {
    if (!this.#drag) return;
    this.#x = event.clientX - this.#drag.x;
    this.#y = event.clientY - this.#drag.y;
    this.#apply();
  }

  #end() {
    this.#drag = null;
  }

  /** Clamp so the frame is always fully covered, then position the media. */
  #apply() {
    if (!this.#media) return;
    const scale = this.#scale;
    const width = this.#natural.width * scale;
    const height = this.#natural.height * scale;
    this.#x = Math.min(0, Math.max(this.#edge - width, this.#x));
    this.#y = Math.min(0, Math.max(this.#edge - height, this.#y));
    // Scale with a transform rather than width/height: the theme sets a global
    // `img { max-width: 100%; height: auto }`, which clamped the width while
    // leaving the height we set, so the photo stretched as it zoomed.
    this.#media.style.transform = `translate(${this.#x}px, ${this.#y}px) scale(${scale})`;
  }

  /** The visible square, at `size` px. */
  toCanvas(size) {
    const scale = this.#scale;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    canvas
      .getContext('2d')
      .drawImage(
        this.#media,
        -this.#x / scale,
        -this.#y / scale,
        this.#edge / scale,
        this.#edge / scale,
        0,
        0,
        size,
        size,
      );
    return canvas;
  }
}

const canvasToJpeg = (canvas, name) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(new File([blob], name, { type: 'image/jpeg' }))
          : reject(new Error('That image could not be processed.')),
      'image/jpeg',
      0.9,
    ),
  );

const INSTAGRAM_PATTERN = /^[A-Za-z0-9._]{1,30}$/;
const INSTAGRAM_URL = /(?:instagram\.com|instagr\.am)\/@?([A-Za-z0-9._]+)/i;

/* Deliberately permissive: the real check is whether the address accepts mail,
   and an over-strict pattern rejects valid addresses people actually own. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

/**
 * The signing form's rules as zod schemas, one per field. They are built from
 * the vendored zod/mini once it loads, and mirror the server's schema — the
 * browser copy exists so people see the problem next to the field; the server
 * copy is the one that decides.
 */
/* Long names ellipsise on the card, so the form stops before that happens. */
const NAME_MAX = 30;

function buildSchemas(z) {
  const optional = (schema) => z.union([z.literal(''), schema]);
  return {
    name: z
      .string()
      .check(z.minLength(1, 'Enter your name.'), z.maxLength(NAME_MAX, `Keep your name under ${NAME_MAX} characters.`)),
    email: z
      .string()
      .check(
        z.minLength(1, 'Enter your email address.'),
        z.regex(EMAIL_PATTERN, "That doesn't look like an email address."),
      ),
    instagram: optional(
      z
        .string()
        .check(
          z.regex(/^\S+$/, "Instagram handles can't contain spaces."),
          z.regex(INSTAGRAM_PATTERN, 'Use letters, numbers, underscores and periods only.'),
        ),
    ),
    message: z.string().check(z.maxLength(MAX_MESSAGE, `Keep it under ${MAX_MESSAGE} characters.`)),
  };
}

/** Accepts a pasted profile URL or an @handle and returns the bare handle. */
const normaliseHandle = (value) => {
  const trimmed = value.trim();
  const fromUrl = trimmed.match(INSTAGRAM_URL);
  return (fromUrl ? fromUrl[1] : trimmed.replace(/^@+/, '')).replace(/\/+$/, '');
};

/*
 * Everyone who picks the same city gets the identical centroid back from
 * Google, so their markers land exactly on top of one another and the map
 * shows one pin where there are twenty. Each signature is offset 20-30m in a
 * random direction: far enough that no two entries share a point and they fan
 * out as you zoom in, small enough to be well under a pixel at city scale, so
 * the placement stays honest.
 *
 * It also means a signature never publishes the precise address someone
 * picked, which matters because every location here is public.
 */
const SCATTER_MIN_M = 20;
const SCATTER_MAX_M = 30;
const METRES_PER_DEGREE = 111_320;

function scatter(lat, lng) {
  const bearing = Math.random() * 2 * Math.PI;
  const distance = SCATTER_MIN_M + Math.random() * (SCATTER_MAX_M - SCATTER_MIN_M);
  // A degree of longitude shrinks towards the poles; guard the division there.
  const shrink = Math.max(Math.cos((lat * Math.PI) / 180), 0.01);
  return {
    lat: lat + (distance * Math.cos(bearing)) / METRES_PER_DEGREE,
    lng: lng + (distance * Math.sin(bearing)) / (METRES_PER_DEGREE * shrink),
  };
}

class GuestbookForm extends HTMLElement {
  #file = null;
  #thumb = null;
  #previewUrl = null;
  /** The full-size image behind the current crop, so it can be re-cropped. */
  #source = null;
  #cropper = null;
  #stream = null;
  #place = null;
  #form = null;
  #fields = new Map();

  connectedCallback() {
    this.endpoint = this.dataset.endpoint.replace(/\/$/, '');
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-file]');
    // The well on the preview card is the photo control; the crop dialog is
    // section-level, because the form itself gets moved into the connect modal.
    this.well = this.querySelector('[data-photo-trigger]');
    this.menu = this.querySelector('[data-photo-menu]');
    this.crop = (this.closest('.guestbook-section') ?? document).querySelector('[data-crop]');
    this.message = this.querySelector('[name="message"]');
    this.count = this.querySelector('[data-count]');
    this.error = this.querySelector('[data-error]');
    this.submit = this.querySelector('[data-submit]');

    this.notice = this.querySelector('[data-notice]');
    this.locationField = this.querySelector('[data-location]');
    this.locationHint = this.querySelector('[data-location-hint]');
    this.preview = this.querySelector('[data-preview-card]');
    this.form.addEventListener('input', () => this.#refreshPreview());
    this.#refreshPreview();

    this.#initPhoto();
    this.input.addEventListener('change', () => {
      this.#openCropFromFile(this.input.files[0]);
      this.input.value = '';
    });
    this.message?.addEventListener('input', () => {
      this.count.textContent = `${this.message.value.length} / ${MAX_MESSAGE}`;
    });
    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      // Location and photo are not form-core fields, so form validation never
      // sees them. Checking them here as well means one tap reports
      // everything that is missing, rather than the text inputs first and the
      // rest only after those are fixed.
      this.#validateExtras();
      await this.#form?.handleSubmit();
      this.#focusFirstInvalid();
    });

    this.#initForm();
    this.#initAutocomplete();
  }

  /**
   * One FormApi, one FieldApi per input. Fields validate on blur and again on
   * submit; a field that has been marked invalid re-validates on every change
   * so the error clears the moment it is fixed — nagging while someone is still
   * typing is what makes forms feel hostile.
   */
  async #initForm() {
    const core = await formCore(this.dataset.formCore);
    const schemas = buildSchemas(core);

    this.#form = new core.FormApi({
      defaultValues: { name: '', email: '', instagram: '', message: '', connectOptIn: false },
      onSubmit: ({ value }) => this.#send(value),
    });
    this.#form.mount();

    for (const input of this.form.querySelectorAll('[name]')) {
      const name = input.name;
      const schema = schemas[name];
      const field = new core.FieldApi({
        form: this.#form,
        name,
        validators: schema ? { onBlur: schema, onSubmit: schema } : undefined,
      });
      field.mount();
      this.#fields.set(name, field);

      const read = () => (input.type === 'checkbox' ? input.checked : input.value);
      input.addEventListener('input', () => {
        field.handleChange(read());
        if (input.getAttribute('aria-invalid') === 'true') field.validate('change');
      });
      input.addEventListener('change', () => field.handleChange(read()));
      input.addEventListener('blur', () => {
        if (name === 'instagram') {
          input.value = normaliseHandle(input.value);
          field.handleChange(input.value);
        }
        field.handleBlur();
      });
      field.store.subscribe(() => this.#reflect(name, field));
    }

    this.#form.store.subscribe(() => {
      setBusy(this.submit, this.#form.state.isSubmitting, this.dataset.submitting || 'Signing…');
    });
  }

  /**
   * After a submit attempt, take the person to the first thing that needs
   * fixing — in the order the fields appear, not the order they validated.
   */
  #focusFirstInvalid() {
    const slot = [...this.querySelectorAll('[data-error-for]')].find((element) => !element.hidden);
    if (!slot) return;
    const name = slot.dataset.errorFor;
    let target = this.querySelector(`[name="${name}"]`);
    if (name === 'photo') target = this.well;
    if (name === 'location') target = this.locationField;
    target?.focus?.({ preventScroll: true });
    target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }

  /** Field state → the error slot below it. */
  #reflect(name, field) {
    const message = field.state.meta.errors.map((error) => error?.message ?? error).find(Boolean) || '';
    this.#setFieldError(name, message);
  }

  #setFieldError(name, message) {
    const slot = this.querySelector(`[data-error-for="${name}"]`);
    const field = this.querySelector(`[name="${name}"]`);
    if (slot) {
      slot.textContent = message || '';
      slot.hidden = !message;
    }
    if (field) {
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
    }
    return !message;
  }

  /** The two inputs TanStack Form does not own: the place and the photo. */
  #validateExtras() {
    let firstInvalid = null;
    const locationOk = this.#setFieldError(
      'location',
      this.#place || this.#locationText() ? null : 'Enter your location.',
    );
    if (!locationOk) firstInvalid = this.locationField;
    const photoOk = this.#setFieldError('photo', this.#file ? null : 'Add a photo for your ID card.');
    if (!(photoOk || firstInvalid)) firstInvalid = this.well;
    return firstInvalid;
  }

  async #initAutocomplete() {
    const field = this.locationField;
    if (!field) return;

    const state = await whenMapsReady();
    if (state !== 'ready') {
      this.#locationNote(MAPS_MESSAGE[state]);
      return;
    }

    const places = await google.maps.importLibrary('places').catch(() => null);

    // PlaceAutocompleteElement is the current API. The older
    // places.Autocomplete has been closed to new Google Cloud projects since
    // March 2025 — on a new key it does nothing at all, which is the most
    // likely reason a location field silently stops suggesting.
    if (places?.PlaceAutocompleteElement) {
      this.#modernAutocomplete(places, field);
      return;
    }

    if (google.maps.places?.Autocomplete) {
      this.#legacyAutocomplete(field);
      return;
    }

    this.#locationNote('Location suggestions are unavailable. Type your location and we will place it later.');
  }

  /** Works for both a plain input and the Places element. */
  #locationText() {
    const field = this.locationField;
    const raw = field?.value ?? field?.querySelector('input')?.value ?? '';
    return raw.trim();
  }

  #locationNote(text) {
    this.locationHint.textContent = text;
    this.locationHint.hidden = !text;
  }

  #applyPlace(address, location, components) {
    const city = components?.find((part) => (part.types || []).includes('locality'));
    const point = scatter(
      typeof location.lat === 'function' ? location.lat() : location.lat,
      typeof location.lng === 'function' ? location.lng() : location.lng,
    );
    this.#place = {
      address,
      city: city?.longText || city?.long_name || '',
      lat: point.lat,
      lng: point.lng,
    };
    this.#setFieldError('location', null);
    this.#locationNote(`Placed at ${address}`);
    this.#refreshPreview();
  }

  #modernAutocomplete(places, field) {
    const element = new places.PlaceAutocompleteElement();
    element.className = 'guestbook-form__places';
    if ('placeholder' in element) element.placeholder = field.placeholder;

    // The element renders its own input, so ours becomes the fallback that is
    // only used if this never initialises.
    field.hidden = true;
    field.removeAttribute('data-location');
    field.after(element);
    this.locationField = element;

    const onSelect = async (event) => {
      const prediction = event.placePrediction || event.detail?.placePrediction;
      const place = prediction ? prediction.toPlace() : event.place || event.detail?.place;
      if (!place) return;
      await place.fetchFields({ fields: ['formattedAddress', 'location', 'addressComponents'] });
      this.#applyPlace(place.formattedAddress, place.location, place.addressComponents);
    };

    // The event was renamed during the element's rollout; accept both.
    element.addEventListener('gmp-select', onSelect);
    element.addEventListener('gmp-placeselect', onSelect);

    this.#locationNote('Start typing, then pick a suggestion to place yourself on the map.');
  }

  #legacyAutocomplete(field) {
    const autocomplete = new google.maps.places.Autocomplete(field, {
      types: ['geocode'],
      fields: ['formatted_address', 'geometry', 'address_components'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) {
        this.#setFieldError('location', 'Pick one of the suggestions so we can place you on the map.');
        return;
      }
      this.#applyPlace(place.formatted_address, place.geometry.location, place.address_components);
    });

    field.addEventListener('input', () => {
      if (this.#place && field.value !== this.#place.address) {
        this.#place = null;
        this.#locationNote('Pick a suggestion to place yourself on the map.');
        this.#refreshPreview();
      }
    });

    this.#locationNote('Start typing, then pick a suggestion to place yourself on the map.');
  }

  /**
   * The card builds as you type. What is shown is exactly what the server
   * will make of the submission, serial aside — that is assigned on signing.
   */
  #refreshPreview() {
    if (!this.preview) return;
    fillCard(this.preview, {
      id: 'hp:PENDING',
      name: this.form.elements.name?.value.trim(),
      location: { address: this.#place?.address || this.#locationText() },
      message: this.form.elements.message?.value.trim(),
      createdAt: Date.now(),
      photo: this.#previewUrl,
    });
  }

  /** Used by the connect flow: the visitor already typed their address. */
  prefillEmail(email) {
    const field = this.querySelector('[name="email"]');
    if (field && email) {
      field.value = email;
      this.#fields.get('email')?.handleChange(email);
    }
  }

  /* ---------- photo ---------- */

  #initPhoto() {
    this.#cropper = new SquareCropper(this.crop?.querySelector('[data-crop-stage]'), {
      onZoom: (value) => {
        const slider = this.crop?.querySelector('[data-crop-zoom]');
        if (slider) slider.value = String(value);
      },
    });

    this.well?.addEventListener('click', () => this.#togglePhotoMenu());
    for (const item of this.querySelectorAll('[data-photo-action]')) {
      item.addEventListener('click', () => this.#photoAction(item.dataset.photoAction));
    }
    // Any click outside closes it, as a menu should.
    document.addEventListener('click', (event) => {
      if (!(this.menu?.hidden || this.contains(event.target))) this.#togglePhotoMenu(false);
    });

    this.crop?.querySelector('[data-crop-cancel]')?.addEventListener('click', () => this.#closeCrop());
    this.crop?.querySelector('[data-crop-save]')?.addEventListener('click', () => this.#useCrop());
    this.crop?.querySelector('[data-crop-shutter]')?.addEventListener('click', () => this.#shutter());
    this.crop?.querySelector('[data-crop-zoom]')?.addEventListener('input', (event) => {
      this.#cropper.zoom(Number(event.target.value));
    });
    this.crop?.addEventListener('click', (event) => {
      if (event.target === this.crop) this.#closeCrop();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.crop?.hidden) this.#closeCrop();
    });
  }

  #togglePhotoMenu(open = this.menu?.hidden) {
    if (!this.menu) return;
    this.menu.hidden = !open;
    this.well?.setAttribute('aria-expanded', String(Boolean(open)));
    if (open) this.#reflectPhotoMenu();
  }

  /** Upload and camera always; adjust and remove only once a photo is set. */
  #reflectPhotoMenu() {
    const has = Boolean(this.#file);
    for (const item of this.querySelectorAll('[data-photo-action]')) {
      const onlyWithPhoto = item.dataset.photoAction === 'recrop' || item.dataset.photoAction === 'remove';
      item.hidden = onlyWithPhoto && !has;
    }
  }

  #photoAction(action) {
    this.#togglePhotoMenu(false);
    if (action === 'upload') this.input.click();
    if (action === 'camera') this.#openCamera();
    if (action === 'recrop' && this.#source) this.#openCrop(this.#source);
    if (action === 'remove') this.#clearPhoto();
  }

  async #openCropFromFile(file) {
    if (!file) return;
    try {
      const image = await new Promise((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('That file is not an image we can read.'));
        element.src = URL.createObjectURL(file);
      });
      this.#openCrop(image);
    } catch (error) {
      this.#showError(error.message);
    }
  }

  /** `source` is a loaded <img>; the camera path supplies one after the shutter. */
  #openCrop(source) {
    if (!this.crop) return;
    this.#stopCamera();
    this.#source = source;
    const image = this.crop.querySelector('[data-crop-image]');
    const video = this.crop.querySelector('[data-crop-video]');
    image.src = source.src;
    image.hidden = false;
    video.hidden = true;
    this.crop.querySelector('[data-crop-shutter]').hidden = true;
    this.crop.querySelector('[data-crop-save]').hidden = false;
    this.crop.querySelector('[data-crop-zoom-row]').hidden = false;
    this.#cropError(null);
    this.crop.hidden = false;
    // The stage has no width until it is shown, and the cropper measures it.
    requestAnimationFrame(() => this.#cropper.attach(image, source.naturalWidth, source.naturalHeight));
  }

  async #openCamera() {
    if (!this.crop) return;
    const video = this.crop.querySelector('[data-crop-video]');
    const image = this.crop.querySelector('[data-crop-image]');
    this.crop.querySelector('[data-crop-shutter]').hidden = false;
    this.crop.querySelector('[data-crop-save]').hidden = true;
    this.crop.querySelector('[data-crop-zoom-row]').hidden = true;
    image.hidden = true;
    video.hidden = false;
    this.#cropError(null);
    this.crop.hidden = false;
    try {
      this.#stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: 'user' } });
      video.srcObject = this.#stream;
      await video.play();
    } catch {
      this.#cropError('No camera available — upload a photo instead.');
    }
  }

  /** Freeze the current frame and hand it to the cropper. */
  #shutter() {
    const video = this.crop?.querySelector('[data-crop-video]');
    if (!video?.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const frame = new Image();
    frame.onload = () => this.#openCrop(frame);
    frame.src = canvas.toDataURL('image/jpeg', 0.92);
  }

  async #useCrop() {
    if (!this.#cropper.ready) return;
    try {
      this.#file = await canvasToJpeg(this.#cropper.toCanvas(PHOTO_EDGE), 'photo.jpg');
      this.#thumb = await canvasToJpeg(this.#cropper.toCanvas(THUMB_EDGE), 'thumb.jpg');
      if (this.#previewUrl) URL.revokeObjectURL(this.#previewUrl);
      this.#previewUrl = URL.createObjectURL(this.#file);
      this.well?.setAttribute('data-has-photo', '');
      this.#setFieldError('photo', null);
      this.#clearError();
      this.#closeCrop();
      this.#refreshPreview();
    } catch (error) {
      this.#cropError(error.message);
    }
  }

  #clearPhoto() {
    this.#file = null;
    this.#thumb = null;
    this.#source = null;
    if (this.#previewUrl) URL.revokeObjectURL(this.#previewUrl);
    this.#previewUrl = null;
    this.well?.removeAttribute('data-has-photo');
    this.#refreshPreview();
  }

  #closeCrop() {
    this.#stopCamera();
    this.#cropper.detach();
    if (this.crop) this.crop.hidden = true;
  }

  #cropError(text) {
    const slot = this.crop?.querySelector('[data-crop-error]');
    if (!slot) return;
    slot.textContent = text ?? '';
    slot.hidden = !text;
  }

  #stopCamera() {
    for (const track of this.#stream?.getTracks() || []) track.stop();
    this.#stream = null;
  }

  #showError(text) {
    this.error.textContent = text;
    this.error.hidden = false;
  }

  #clearError() {
    this.error.hidden = true;
  }

  /** Runs once every field validator has passed. */
  async #send(value) {
    this.#clearError();
    if (this.#validateExtras()) return;

    const data = new FormData(this.form);
    data.set('name', value.name.trim());
    data.set('email', value.email.trim());
    data.set('instagram', normaliseHandle(value.instagram || ''));
    data.set('message', value.message.trim());
    data.set('connectOptIn', value.connectOptIn ? 'true' : 'false');
    data.set('photo', this.#file);
    if (this.#thumb) data.set('thumb', this.#thumb);
    if (this.#place) {
      data.set('address', this.#place.address);
      data.set('city', this.#place.city);
      data.set('lat', String(this.#place.lat));
      data.set('lng', String(this.#place.lng));
    } else if (this.#locationText()) {
      // No suggestion picked, so there are no coordinates. The entry is still
      // valid — it just sits in the list rather than on the map until geocoded,
      // exactly like the 45 legacy entries in the same position.
      data.set('address', this.#locationText());
    }

    try {
      const response = await fetch(`${this.endpoint}/api/v1/entries`, { method: 'POST', body: data });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || `HTTP ${response.status}`);

      this.#stopCamera();
      this.form.reset();
      this.#form.reset();
      this.#file = null;
      this.#thumb = null;
      this.#place = null;
      this.#clearPhoto();
      if (this.onSigned) {
        this.onSigned(body);
      } else {
        document.querySelector('guestbook-app')?.addEntry(body);
      }
    } catch (error) {
      this.#showError(error.message);
    }
  }
}

customElements.define('guestbook-form', GuestbookForm);

/*
 * The L.E.D. site's top-right clock, ported from ui/Time.tsx:
 * "HH:MM:SS / AMERICA / NEW YORK" over "VIEWING: <page>". Timezone comes
 * from the visitor's browser, exactly as the original did.
 */
class GuestbookClock extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.time = this.querySelector('[data-time]');
    this.zone = this.querySelector('[data-zone]');

    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    this.zone.textContent = zone.replace(/_/g, ' ').replace(/\//g, ' / ');

    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#timer);
  }

  #tick() {
    const now = new Date();
    const pad = (value) => String(value).padStart(2, '0');
    this.time.textContent = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  }
}

customElements.define('guestbook-clock', GuestbookClock);
